import { Component, type ReactNode } from 'react'
import { Icon, type IconName } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'

export interface WidgetErrorBoundaryProps {
  title: string
  icon: IconName
  children: ReactNode
}

interface WidgetErrorBoundaryState {
  hasError: boolean
}

/**
 * The last line of defence for AC26's "one widget's failure never blanks
 * the rest of the dashboard" — `WidgetFrame` only contains a *reported*
 * fetch failure (`status === 'error'`); it cannot contain a widget that
 * throws while rendering (e.g. an API-contract drift the widget didn't
 * defend against — exactly IB-162's root cause). `DashboardScreen` wraps
 * every widget slot in one of these so a thrown render error degrades to
 * this one contained, on-brand card instead of unmounting the whole
 * `/insights/dashboards` page.
 *
 * Renders the same `iw-widget-card` shell as `WidgetFrame` so a caught
 * failure is visually indistinguishable from a reported one — quiet, never
 * a raw stack trace, never an emoji.
 */
export class WidgetErrorBoundary extends Component<WidgetErrorBoundaryProps, WidgetErrorBoundaryState> {
  state: WidgetErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): WidgetErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: unknown): void {
    // Contained, not silent — surfaces in the console/telemetry pipeline
    // without re-throwing past this boundary.
    console.error(`[WidgetErrorBoundary] "${this.props.title}" failed to render`, error)
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false })
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }
    return (
      <section className="iw-widget-card" aria-label={this.props.title}>
        <div className="iw-widget-card__header">
          <span className="iw-widget-card__icon">
            <Icon name={this.props.icon} size={18} />
          </span>
          <h2 className="iw-widget-card__title">{this.props.title}</h2>
        </div>
        <InlineAlert
          severity="error"
          action={
            <button type="button" className="iw-btn iw-btn--sm" onClick={this.handleRetry}>
              Try again
            </button>
          }
        >
          This tile could not load. The rest of the dashboard is unaffected.
        </InlineAlert>
      </section>
    )
  }
}
